import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import { useCanMutate } from '../../../hooks/useCanMutate';
import produccionLineaService from '../../../services/produccionLineaService';

const emptyForm = () => ({
  name: '',
  description: '',
  is_active: true,
});

const ProduccionLineasIndex = () => {
  const canMutate = useCanMutate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      const res = await produccionLineaService.list(params);
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRows([]);
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar líneas',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      is_active: Boolean(row.is_active),
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setToast({ isOpen: true, message: 'Indique el nombre de la línea', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        is_active: form.is_active,
      };
      const res = editing
        ? await produccionLineaService.update(editing.id, payload)
        : await produccionLineaService.create(payload);
      if (res.success) {
        setToast({
          isOpen: true,
          message: editing ? 'Línea actualizada' : 'Línea creada',
          type: 'success',
        });
        closeForm();
        await loadList();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo guardar', type: 'error' });
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al guardar',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      const res = await produccionLineaService.remove(deleteTarget.id);
      if (res.success) {
        setToast({ isOpen: true, message: 'Línea eliminada', type: 'success' });
        setDeleteTarget(null);
        await loadList();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo eliminar', type: 'error' });
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al eliminar',
        type: 'error',
      });
    }
  };

  const toggleActive = async (row) => {
    if (!canMutate) return;
    try {
      const res = await produccionLineaService.update(row.id, { is_active: !row.is_active });
      if (res.success) {
        await loadList();
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'No se pudo cambiar el estado',
        type: 'error',
      });
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/produccion" className="hover:text-teal-600">Producción</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Líneas de producción</span>
      </nav>

      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Líneas de producción</h1>
          <p className="text-sm text-gray-600 mt-1">
            Agrupe recetas por línea y filtre las guías de producción según dónde se elabora cada producto.
          </p>
        </div>
        {canMutate && (
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium"
          >
            + Nueva línea
          </button>
        )}
      </div>

      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o código..."
          className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {formOpen && (
        <form onSubmit={handleSave} className="mb-6 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-3">
            {editing ? `Editar: ${editing.code}` : 'Nueva línea'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                maxLength={160}
                required
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
                Activa
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button type="button" onClick={closeForm} className="px-4 py-2 border border-gray-300 rounded-md text-sm">
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <p className="p-6 text-center text-gray-500">Cargando…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-gray-500">No hay líneas de producción</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Código</th>
                <th className="text-left px-4 py-3 font-medium">Nombre</th>
                <th className="text-left px-4 py-3 font-medium">Recetas</th>
                <th className="text-left px-4 py-3 font-medium">Estado</th>
                <th className="text-left px-4 py-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-teal-700">{row.code}</td>
                  <td className="px-4 py-3">{row.name}</td>
                  <td className="px-4 py-3 text-gray-600">{row.recetas_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {row.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canMutate ? (
                      <div className="flex flex-wrap gap-3">
                        <button type="button" onClick={() => openEdit(row)} className="text-teal-700 hover:underline">
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(row)}
                          className="text-amber-700 hover:underline"
                        >
                          {row.is_active ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(row)}
                          className="text-red-600 hover:underline"
                        >
                          Borrar
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar línea"
        message={
          deleteTarget
            ? `¿Eliminar la línea «${deleteTarget.name}»? Las recetas quedarán sin línea asignada.`
            : ''
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default ProduccionLineasIndex;
